<template>
    <div class="w-100 overflow-hidden">

        <div class="row no-gutters" style="height: 700px; max-height: 700px;">

            <div class="col-3 h-100 bg-gray-100">
                <div class="d-flex flex-column h-100">

                    <div class="border-bottom bg-gray-200 d-flex align-items-center justify-content-center" style="min-height: 50px; height: 50px;">
                        <b-form-radio-group
                            id="snippet-shared-filter"
                            v-model="sharedFilter"
                            :options="sharedFilterOptions"
                            name="radios-btn-shared-filter"
                            buttons
                            button-variant="minimal-secondary"
                            size="sm"
                        ></b-form-radio-group>
                    </div>

                    <div class="flex-fill overflow-auto">
                        
                        <b-list-group 
                            v-if="snippetsFiltered.length" 
                            key="hasSnippets"
                            flush 
                            class="border-top border-bottom shadow-sm"
                        >
                            <b-list-group-item
                                v-for="snip in snippetsFiltered" 
                                :key="snip._localId" 
                                class="font-size-80 font-weight-500 d-flex align-items-center justify-content-between"
                                href="#"
                                :active="activeSnippetLocalId === snip._localId"
                                @click="setActiveSnippet(snip._localId)"
                            >
                                <div class="">
                                    {{ snip.name ? snip.name : 'New Snippet' }}
                                </div>
                                <div class="d-flex align-items-center">
                                    <b-icon
                                        v-if="!snip.id"
                                        icon="exclamation-circle"
                                        variant="danger"
                                        v-b-tooltip.hover.topleft="`Unsaved`"
                                    ></b-icon>
                                    <b-icon 
                                        v-if="snip.is_shared"  
                                        icon="people-fill" 
                                        v-b-tooltip.hover.topleft="'Shared'"
                                    ></b-icon>  <!-- don't add variant to this... then active styling is wierd -->
                                </div>
                                
                            </b-list-group-item>
                        </b-list-group>

                        <div v-else key="emptySnippetList" class="py-4">
                            <h6 class="text-muted text-center">No Snippets</h6>
                        </div>

                    </div>
                   
                
                    <div class="border-top" style="min-height: 50px; height: 50px;">
                        <b-button v-if="isEdit" key="addButton" variant="zone-primary" @click="addSnippet">
                            <b-icon icon="plus-circle" class="mr-2"></b-icon>
                            New Snippet
                        </b-button>
                        <div v-if="isInsert" key="toManage" class="h-100 w-100">
                            <b-button v-if="manageSnippetsUrl" variant="zone-primary" :href="manageSnippetsUrl" target="_blank" class="d-flex align-items-center justify-content-center">
                                <b-icon icon="box-arrow-up-right" class="mr-2"></b-icon>
                                Manage Snippets
                            </b-button>
                            <div v-else class="h-100 w-100 px-2 py-1 text-center bg-primary-100 d-flex align-items-center justify-content-center">
                                <div class="small text-muted">Snippets can be added by your company admins</div>
                            </div>
                        </div>
                    </div>
                </div>
                
            </div>

            <div class="col-9 border-left h-100 bg-white">
                <transition name="fade-fast" mode="out-in">
                    <snippet-editor
                        v-if="activeSnippet._localId"
                        :key="activeSnippet._localId"
                        :snippet="activeSnippet"
                        :supportsHtml="areaData.supports_html"
                        :mode="mode"
                        :disabled="!canManageShared && activeSnippet.is_shared"
                        @update="handleUpdate"
                        @request-delete="handleRequestDelete(activeSnippet._localId)"
                        @insert="handleInsert"
                    ></snippet-editor>
                    <div v-else class="d-flex align-items-center justify-content-center" style="min-height: 400px;">
                        <transition name="fade-fast" mode="out-in">
                            <div v-if="loadingArea || loadingSnippets || !initialSnippetsLoadComplete" key="loadingSnippets" class="mb-0">
                                <b-spinner></b-spinner>
                            </div>
                            <div v-else-if="initialSnippetsLoadComplete && !hasArea" key="noArea" class="mb-0 font-size-100 font-weight-bold text-muted text-center">
                                No Snippets
                                <div class="small font-weight-400 mt-2">
                                    Snippets in this area are not enabled for your organization.<br>
                                    Contact Supplios Support to enable them.<br>
                                    (snippet area reference: {{ areaSlug }})
                                </div>
                            </div>
                            <div v-else key="noSnippets" class="mb-0 font-size-100 font-weight-bold text-muted">
                                No Snippets
                            </div>
                        </transition>
                    </div>
                </transition>
            </div>
            
        </div>

    </div>
</template>

<script>
import { errorHandling, currentUserInfoMixin } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import SnippetEditor from './SnippetEditor.vue';
//import { random22id } from '../../js/filters-helpers';
import uniqueId from 'lodash/uniqueId';
import orderBy from 'lodash/orderBy';

export default {
    name: 'SnippetManager',
    components: {
        SnippetEditor,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        areaSlug: {  // currently only for managing snippets for a SINGLE area
            type: String,
            required: true,
        },
        mode: {  // options are 'insert' and 'edit'  
            type: String,
            default: 'insert',
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingArea: false, 
            loadingSnippets: false,
            loadingNewSnippet: false,
            initialAreaLoadComplete: false,  // will set true after first api call whether we got area or not
            initialSnippetsLoadComplete: false,
            areaData: {},  // for a single area... maybe change later - or maybe make this a prop?  fetch areas in parent?
            snippets: [],  
            snippetStore: {},  // our local 'store' keyed by _localId
            activeSnippet: {},
            // filters
            sharedFilter: 'all',  // all is default 
            sharedFilterOptions: [
                { value: 'all', text: 'All' },
                { value: 'shared', text: 'Shared' },
                { value: 'private', text: 'Private' },
            ],
        }
    },
    computed: {
        areaId() {
            return this.areaData ? this.areaData.id : '';
        },
        hasArea() {
            // used to return something when we have the area loaded
            // and/or to indicate a environment doesn't have a specific area enabled  (snippet areas are not tenant specific at this point)
            return !!this.areaId;
        },
        snippetsOrdered() {
            // we show them in reverse order... they'll come from API sorted by created date, latest last - reverse it here
            // then we give them _position values in same order, and when we make a new one, add to last position, which means it will show up first from here
            return orderBy(Object.values(this.snippetStore), ['_position'], ['desc']);
        },
        snippetsFiltered() {
            // update based on the sharedFilter selection 
            if (this.sharedFilter === 'shared') {
                return this.snippetsOrdered.filter(snip => snip.is_shared);
            } else if (this.sharedFilter === 'private') {
                return this.snippetsOrdered.filter(snip => !snip.is_shared);
            }
            return this.snippetsOrdered;
        },
        activeSnippetLocalId() {
            return this.activeSnippet._localId ?? '';
        },
        nextPosition() {
            const positions = this.snippetsOrdered.map(snip => Number(snip._position));
            return positions.length ? Math.max(...positions) + 1 : 1;
        },
        canManageShared() {
            // can the current user share / manage shared / create shared snippets?
            return this.$currentUserInfo.is_tenant_admin;
        },
        isEdit() {
            return this.mode === 'edit';
        },
        isInsert() {
            return this.mode === 'insert';
        },
        manageSnippetsUrl() {
            // IF the current TU can manage snippets, we get 'manage_snippets_url' in areaData from backend
            // show a link to that if in insert mode, at least until you allow switching to edit mode from insert mode
            return this.areaData?.manage_snippets_url || '';  // relative URL
        },
    },
    mounted() {
        this.loadInitial();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async loadInitial() {
            await this.getArea();
            await this.getSnippets();
        },
        async getArea() {
            this.loadingArea = true;
            let resp;
            try {
                // snippetArea viewset uses slug as lookup, not ID
                resp = await suppliosApi.snippetArea.retrieve(this.areaSlug);
            } catch (error) {
                if (error.response.status === 404) {
                    // if specifically not found, could be this tenant doesn't have this snippet area
                    // just fail gracefully... don't show error message
                    this.areaData = {};  // should already be this, but in case it gets removed...
                } else {
                    // otherwise, if we have some other error (auth, etc.) then show error message
                    this.apiErrorHandler(error);
                }
            }
            if (resp && resp.data) {
                this.areaData = resp.data;
            }
            this.initialAreaLoadComplete = true;  // set true whether we got area data or not
            this.loadingArea = false;
        },
        async getSnippets() {
            if (!this.hasArea) {  // make sure this is AFTER getArea() is DONE (use await)
                console.log('areaData is not loaded, so no snippet area for this tenant or not loaded yet');
                this.initialSnippetsLoadComplete = true;  // set this true to make template logic work 
                return;
            }
            this.loadingSnippets = true;
            let resp;
            const axiosConfig = {
                params: {
                    areas__slug: this.areaSlug,  // django filters viewset syntax
                }
            };
            try {
                resp = await suppliosApi.snippet.list(axiosConfig);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp && resp.data) {
                // NOT PAGINATED YET - no pagination controls here
                const snippets = resp.data.map((snip, idx) => {
                    return {...snip, _localId: uniqueId(), _position: idx + 1};
                });  // response is paginated 
                const lastSnippet = snippets.length ? snippets[snippets.length - 1] : null;
                // SET THE STORE
                this.snippetStore = Object.fromEntries(snippets.map(snip => ([snip._localId, snip])));
                if (lastSnippet) {
                    this.setActiveSnippet(lastSnippet._localId);
                }
            }
            this.initialSnippetsLoadComplete = true;
            this.loadingSnippets = false;
        },
        async performDelete(snippetDbId) {
            // this is only for snippets already saved to DB - must have DB id (pass it in)
            let resp;
            try {
                resp = await suppliosApi.snippet.destroy(snippetDbId);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            return !!resp;
        },
        removeSnippetFromLocal(localId) {
            // remove a snippet from the local store - either do this just after api delete call, or directly if it was never saved
            this.$delete(this.snippetStore, localId); 
            // now find some other snippet to select 
            const selectSnip = this.snippetsFiltered.length ? this.snippetsFiltered[0] : null;
            if (selectSnip) {
                this.setActiveSnippet(selectSnip._localId);
            } else {
                this.setActiveSnippet('');  // reset it 
            }
        },
        async addSnippet() {
            // just make a new one, push into array, let editor component make API call to create it
            const newSnippet = {
                // don't pass in id... that's how child knows it is new
                name: '',  // needs name before it can be saved
                content: '',
                is_shared: ((this.sharedFilter === 'shared') && this.canManageShared),
                areas: [this.areaId],
                _localId: uniqueId(),
                _position: this.nextPosition,  // _position is 1-based, must get from max of all other positions, in case some got deleted from middle positions
            };
            this.$set(this.snippetStore, newSnippet._localId, newSnippet);
            this.setActiveSnippet(newSnippet._localId);
        },
        setActiveSnippet(localId) {
            // if you pass in '' or other falsey value, we set this.activeSnippet to {}  (reset)
            if (!localId) {
                this.activeSnippet = {};
            }
            this.activeSnippet = this.snippetStore[localId] ?? {};
        },
        handleUpdate(updatedSnippet) {
            // updatedSnippet will be the snippet object, with _localId, but no content
            const localId = updatedSnippet._localId;
            const localSnippet = this.snippetStore[localId];
            if (localSnippet) {
                this.$set(this.snippetStore, localId, updatedSnippet);
            }
        },
        async handleRequestDelete(localId) {
            // do the delete here, requested in child comp
            console.log('request-delete from', localId);
            const snippet = this.snippetStore[localId];
            const snippetDatabaseId = snippet.id;
            if (snippet.is_shared && !this.canManageShared) {
                console.log('ignoring delete request - user cannot manage shared snippets');
                return;
            }
            const canProceed = await this.$bvModal.msgBoxConfirm('Delete this snippet?  It will be deleted immediately.', {
                title: 'Please Confirm',
                size: 'sm',
                centered: true,
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Delete',
                cancelTitle: 'No, Cancel',
            });
            if (canProceed) {
                if (snippetDatabaseId) {
                    // if it has .id, then it is already saved... make API call
                    const deleteOk = await this.performDelete(snippetDatabaseId);
                    if (deleteOk) {
                        this.removeSnippetFromLocal(localId);
                    }
                } else {
                    // if NOT saved, just remove from store... see ya
                    this.removeSnippetFromLocal(localId);
                }
            }
        },
        // INSERTING CONTENT
        handleInsert(content) {
            // handle @insert event from single snippet editor/viewer
            this.$emit('insert', content);  // just relay up for now
        }
    }
}
</script>

<style lang="scss">

#snippet-shared-filter {
    .btn.active {
        color: var(--primary-darker);
    }
}

</style>